import { Box, Card, CardContent, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import useBackendApi from '../hooks/useBackendApi';
import { useNavigate } from 'react-router-dom';
import { getNumberFromGID } from '../utils';
import { RequestOptions } from '../types/httpRequests';

interface OrderDetailsInterface {
  order_date: string;
  total_quantity: number;
  order_status: string;
  s_order_id: string;
  order_name: string;
  total_amount: string;
}

const backendURL = process.env.REACT_APP_BACKEND_URL;

function OrdersList() {
  const [orders, setOrders] = useState<OrderDetailsInterface[]>([]);
  const { isLoading, apiFetchRequest } = useBackendApi();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const endpointUrl = `${backendURL}/orders/`;

  useEffect(() => {
    const fetchOrders = async () => {
      const apiRequestOptions: RequestOptions = {
        endpoint: endpointUrl,
      };

      try {
        const data = await apiFetchRequest(apiRequestOptions);
        if (data.status !== 200) {
          return navigate('/404.html');
        }
        setOrders(data.body.orders);
      } catch (err) {
        console.error('Error fetching orders', err);
      }
    };

    fetchOrders();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const onClickOrderDetails = (orderId: string) => {
    return navigate(`/orders/${orderId}`);
  };

  const renderOrderDetails = (order: OrderDetailsInterface) => {
    const shopifyOrderId = getNumberFromGID(order.s_order_id);
    return (
      <Grid
        item
        xs={12}
        key={shopifyOrderId}
        md={isDesktop ? 4 : 12}
        onClick={() => onClickOrderDetails(shopifyOrderId)}
        sx={{
          cursor: 'pointer',
        }}
      >
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6">{order.order_name}</Typography>
            <Typography variant="body2">Date: {formatDate(order.order_date)}</Typography>
            <Typography variant="body2">Status: {order.order_status}</Typography>
            <Typography variant="body2">Quantity: {order.total_quantity}</Typography>
            <Typography variant="body2">Total: {order.total_amount}</Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };
  const renderNoOrdersAvailable = () => {
    if (orders.length !== 0) return;

    return <Typography variant="h6">No orders available</Typography>;
  };

  return (
    <Box sx={{ padding: theme.spacing(2) }}>
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {renderNoOrdersAvailable()}
        {orders.map((order) => renderOrderDetails(order))}
      </Grid>
    </Box>
  );
}

export default OrdersList;
